import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box, Typography, Stack, Chip, Button, IconButton,
  Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, MenuItem, Select, FormControl, InputLabel,
  Autocomplete, CircularProgress, Alert, Tooltip,
  List, ListItem, ListItemText,
  Accordion, AccordionSummary, AccordionDetails, Divider,
  Grid2 as Grid,
  Tabs, Tab, Paper, Table, TableHead, TableBody, TableRow, TableCell,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Save as SaveIcon,
  ExpandMore as ExpandMoreIcon,
  Campaign as DisseminationIcon,
  Event as EventIcon,
  Article as ArticleIcon,
  Web as WebIcon,
  Policy as PolicyIcon,
  Science as ScienceIcon,
  AddCircleOutline as AddEventIcon,
  Groups as GroupsIcon,
  RecordVoiceOver as CommIcon,
  LibraryBooks as KnowledgeIcon,
  Bolt as ExploitIcon,
} from '@mui/icons-material';
import { axiosService } from 'shared/helpers/axios';
import { useLocale } from 'shared/contexts/LocaleContext';
import EventFormDialog from 'components/collab/events/EventFormDialog';
import {useTheme} from '@mui/material/styles';


const CATEGORIES = [
  { value: 'journal_article', label: 'Journal Article', icon: <ArticleIcon fontSize="small" /> },
  { value: 'conference_paper', label: 'Conference Paper', icon: <ArticleIcon fontSize="small" /> },
  { value: 'conference_presentation', label: 'Conference Presentation', icon: <EventIcon fontSize="small" /> },
  { value: 'event', label: 'Event Participation', icon: <EventIcon fontSize="small" /> },
  { value: 'workshop', label: 'Workshop', icon: <ScienceIcon fontSize="small" /> },
  { value: 'webinar', label: 'Webinar', icon: <WebIcon fontSize="small" /> },
  { value: 'social_media', label: 'Social Media', icon: <DisseminationIcon fontSize="small" /> },
  { value: 'website', label: 'Website / Blog', icon: <WebIcon fontSize="small" /> },
  { value: 'press_release', label: 'Press Release', icon: <ArticleIcon fontSize="small" /> },
  { value: 'policy_brief', label: 'Policy Brief', icon: <PolicyIcon fontSize="small" /> },
  { value: 'dataset', label: 'Open Dataset', icon: <ScienceIcon fontSize="small" /> },
  { value: 'software', label: 'Open Software', icon: <ScienceIcon fontSize="small" /> },
  { value: 'patent', label: 'Patent', icon: <ArticleIcon fontSize="small" /> },
  { value: 'other', label: 'Other', icon: <DisseminationIcon fontSize="small" /> },
];

const AUDIENCES = [
  { value: 'academic', label: 'Academic' },
  { value: 'industry', label: 'Industry' },
  { value: 'policy', label: 'Policy Makers' },
  { value: 'public', label: 'General Public' },
  { value: 'other', label: 'Other' },
];

const EVENT_TYPES_THAT_NEED_EVENT = new Set(['event', 'conference_presentation', 'workshop', 'webinar']);

const CATEGORY_ICON = Object.fromEntries(CATEGORIES.map(c => [c.value, c.icon]));

// ── ActivityDialog ────────────────────────────────────────────────────────────
function ActivityDialog({ open, onClose, deliverable, activity, onSaved }) {
  const { formatDate } = useLocale();
  const isEdit = Boolean(activity?.id);
  const [form, setForm] = useState({
    category: '',
    title: '',
    description: '',
    target_audience: '',
    target_audience_other: '',
    event: null,
  });
  const [eventSearch, setEventSearch] = useState('');
  const [eventOptions, setEventOptions] = useState([]);
  const [loadingEvents, setLoadingEvents] = useState(false);
  const [createEventOpen, setCreateEventOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!open) return;
    if (isEdit) {
      setForm({
        category: activity.category || '',
        title: activity.title || '',
        description: activity.description || '',
        target_audience: activity.target_audience || '',
        target_audience_other: activity.target_audience_other || '',
        event: activity.event ? { id: activity.event, title: activity.event_title } : null,
      });
    } else {
      setForm({ category: '', title: '', description: '', target_audience: '', target_audience_other: '', event: null });
    }
    setError(null);
  }, [open, activity, isEdit]);

  useEffect(() => {
    if (!EVENT_TYPES_THAT_NEED_EVENT.has(form.category)) return;
    const q = eventSearch.trim();
    setLoadingEvents(true);
    axiosService.get('/events/', { params: { search: q, ordering: 'start_date', page_size: 20 } })
      .then(res => setEventOptions(res.data?.results || res.data || []))
      .catch(() => setEventOptions([]))
      .finally(() => setLoadingEvents(false));
  }, [eventSearch, form.category]);

  const set = (field) => (e) => setForm(f => ({ ...f, [field]: e.target.value }));

  const handleSave = async () => {
    if (!form.category) { setError('Category is required'); return; }
    setSaving(true);
    setError(null);
    try {
      const payload = {
        deliverable: deliverable.id,
        category: form.category,
        title: form.title || null,
        description: form.description || null,
        target_audience: form.target_audience || null,
        target_audience_other: form.target_audience === 'other' ? (form.target_audience_other || null) : null,
        event: form.event?.id || null,
      };
      if (isEdit) {
        await axiosService.patch(`/proposal/dissemination/${activity.id}/`, payload);
      } else {
        await axiosService.post('/proposal/dissemination/', payload);
      }
      onSaved();
      onClose();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to save. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const needsEvent = EVENT_TYPES_THAT_NEED_EVENT.has(form.category);

  if (!deliverable) return null;

  return (
    <>
      <EventFormDialog
        open={createEventOpen}
        onClose={() => setCreateEventOpen(false)}
        zIndex={3200}
        onSaved={(newEvent) => {
          setEventOptions(prev => [newEvent, ...prev]);
          setForm(f => ({ ...f, event: newEvent }));
        }}
      />
      <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth sx={{ zIndex: 3100 }}>
        <DialogTitle>
          {isEdit ? 'Edit Dissemination Activity' : 'Add Dissemination Activity'}
          <Typography variant="caption" display="block" color="text.secondary">
            {deliverable.title || `D${deliverable.wp_number}.${deliverable.number}`}
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ pt: 1 }}>
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          <Stack spacing={2} sx={{ mt: 1 }}>
            <FormControl fullWidth size="small">
              <InputLabel>Category *</InputLabel>
              <Select value={form.category} label="Category *" onChange={set('category')}>
                {CATEGORIES.map(c => (
                  <MenuItem key={c.value} value={c.value}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {c.icon} {c.label}
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField label="Title" size="small" fullWidth value={form.title} onChange={set('title')} />

            {needsEvent && (
              <Box>
                <Autocomplete
                  size="small"
                  options={eventOptions}
                  getOptionLabel={o => o.title || ''}
                  loading={loadingEvents}
                  value={form.event}
                  onChange={(_, val) => setForm(f => ({ ...f, event: val }))}
                  onInputChange={(_, val) => setEventSearch(val)}
                  renderInput={(params) => (
                    <TextField {...params} label="Link to Event (optional)"
                      slotProps={{ input: { ...params.InputProps, endAdornment: (<>{loadingEvents && <CircularProgress size={14} />}{params.InputProps.endAdornment}</>) } }} />
                  )}
                  renderOption={(props, option) => (
                    <li {...props} key={option.id}>
                      <Box>
                        <Typography variant="body2">{option.title}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {option.event_type_display} · {option.location || 'Online'} · {option.start_date ? formatDate(option.start_date) : '—'}
                        </Typography>
                      </Box>
                    </li>
                  )}
                />
                <Button
                  size="small"
                  startIcon={<AddEventIcon />}
                  onClick={() => setCreateEventOpen(true)}
                  sx={{ mt: 0.5, textTransform: 'none', fontSize: '0.75rem' }}
                >
                  Add New Event
                </Button>
              </Box>
            )}

            <TextField label="Description" size="small" fullWidth multiline rows={2} value={form.description} onChange={set('description')} />

            <FormControl size="small" fullWidth>
              <InputLabel>Target Audience</InputLabel>
              <Select value={form.target_audience} label="Target Audience" onChange={set('target_audience')}>
                <MenuItem value=""><em>None</em></MenuItem>
                {AUDIENCES.map(a => <MenuItem key={a.value} value={a.value}>{a.label}</MenuItem>)}
              </Select>
            </FormControl>
            {form.target_audience === 'other' && (
              <TextField
                label="Specify audience *"
                size="small"
                fullWidth
                value={form.target_audience_other}
                onChange={set('target_audience_other')}
                placeholder="Describe the target audience…"
              />
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="contained" onClick={handleSave} disabled={saving}>
            {saving ? <CircularProgress size={18} /> : isEdit ? 'Save' : 'Add'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

// ── KER dialog ───────────────────────────────────────────────────────────────
const TRL_OPTIONS = Array.from({ length: 9 }, (_, i) => i + 1);
const EXPLOITATION_ROUTES = [
  { value: 'licensing', label: 'Licensing' },
  { value: 'spin_off', label: 'Spin-off / Start-up' },
  { value: 'product', label: 'Product / Service development' },
  { value: 'standard', label: 'Standardisation' },
  { value: 'policy', label: 'Policy uptake' },
  { value: 'open_source', label: 'Open-source release' },
  { value: 'publication', label: 'Scientific publication' },
  { value: 'other', label: 'Other' },
];

const EMPTY_KER = { title: '', description: '', trl_current: '', trl_target: '', exploitation_route: '', owner: '' };

function KERDialog({ open, onClose, ker, onSave }) {
  const [form, setForm] = useState(EMPTY_KER);
  useEffect(() => { setForm(ker ? { ...EMPTY_KER, ...ker } : EMPTY_KER); }, [open, ker]);
  const set = (f) => (e) => setForm(p => ({ ...p, [f]: e.target.value }));
  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth sx={{ zIndex: 3100 }}>
      <DialogTitle>{ker ? 'Edit Key Exploitable Result' : 'Add Key Exploitable Result'}</DialogTitle>
      <DialogContent sx={{ pt: 1 }}>
        <Stack spacing={2} sx={{ mt: 0.5 }}>
          <TextField label="Title *" size="small" fullWidth value={form.title} onChange={set('title')} />
          <TextField label="Description" size="small" fullWidth multiline rows={2} value={form.description} onChange={set('description')} />
          <Grid container spacing={2}>
            <Grid size={6}>
              <FormControl fullWidth size="small">
                <InputLabel>Current TRL</InputLabel>
                <Select value={form.trl_current} label="Current TRL" onChange={set('trl_current')}>
                  <MenuItem value=""><em>—</em></MenuItem>
                  {TRL_OPTIONS.map(n => <MenuItem key={n} value={n}>TRL {n}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
            <Grid size={6}>
              <FormControl fullWidth size="small">
                <InputLabel>Target TRL</InputLabel>
                <Select value={form.trl_target} label="Target TRL" onChange={set('trl_target')}>
                  <MenuItem value=""><em>—</em></MenuItem>
                  {TRL_OPTIONS.map(n => <MenuItem key={n} value={n}>TRL {n}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
          <FormControl fullWidth size="small">
            <InputLabel>Exploitation Route</InputLabel>
            <Select value={form.exploitation_route} label="Exploitation Route" onChange={set('exploitation_route')}>
              <MenuItem value=""><em>None</em></MenuItem>
              {EXPLOITATION_ROUTES.map(r => <MenuItem key={r.value} value={r.value}>{r.label}</MenuItem>)}
            </Select>
          </FormControl>
          <TextField label="Owner / Responsible Partner" size="small" fullWidth value={form.owner} onChange={set('owner')} />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={() => { if (form.title) onSave(form); }}>
          {ker ? 'Save' : 'Add'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ── PlanSections ─────────────────────────────────────────────────────────────
const PLAN_TABS = [
  { key: 'target_groups',          label: 'Target Groups',           icon: <GroupsIcon fontSize="small" /> },
  { key: 'communication_plan',     label: 'Communication Plan',      icon: <CommIcon fontSize="small" /> },
  { key: 'knowledge_dissemination',label: 'Knowledge Dissemination', icon: <KnowledgeIcon fontSize="small" /> },
];

const PLAN_HINTS = {
  target_groups: 'Identify the key target groups for dissemination activities — academic community, industry partners, policy makers, general public, etc. Describe their relevance and how you plan to reach them.',
  communication_plan: 'Describe the overall communication strategy: channels (website, social media, press releases, newsletters), timeline, messaging framework, and responsible partners.',
  knowledge_dissemination: 'Specify planned publications (journals, conference papers, open access), events (conferences, workshops, seminars, webinars), training activities, and any magazines or popular science outreach.',
};

function PlanSections({ proposal, onPlanSaved }) {
  const plan = proposal?.dissemination_plan || {};
  const [planTab, setPlanTab] = useState(0);
  const [texts, setTexts] = useState({
    target_groups: plan.target_groups || '',
    communication_plan: plan.communication_plan || '',
    knowledge_dissemination: plan.knowledge_dissemination || '',
  });
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const saveTimer = useRef(null);

  useEffect(() => {
    const p = proposal?.dissemination_plan || {};
    setTexts({
      target_groups: p.target_groups || '',
      communication_plan: p.communication_plan || '',
      knowledge_dissemination: p.knowledge_dissemination || '',
    });
  }, [proposal?.id]);

  const currentKey = PLAN_TABS[planTab].key;

  const save = useCallback(async (nextTexts) => {
    if (!proposal?.id) return;
    setSaving(true);
    try {
      await axiosService.patch(`/proposal/proposals/${proposal.id}/`, {
        dissemination_plan: {
          ...(proposal?.dissemination_plan || {}),
          ...nextTexts,
        },
      });
      setSaved(true);
      if (onPlanSaved) onPlanSaved();
      clearTimeout(saveTimer.current);
      saveTimer.current = setTimeout(() => setSaved(false), 2000);
    } catch (_) {}
    finally { setSaving(false); }
  }, [proposal?.id, proposal?.dissemination_plan, onPlanSaved]);

  const handleTextChange = (e) => setTexts(t => ({ ...t, [currentKey]: e.target.value }));

  return (
    <Paper variant="outlined" sx={{ mb: 3, borderRadius: 2, overflow: 'hidden' }}>
      <Tabs
        value={planTab}
        onChange={(_, v) => setPlanTab(v)}
        variant="scrollable"
        scrollButtons="auto"
        sx={{ borderBottom: 1, borderColor: 'divider', bgcolor: 'background.default' }}
      >
        {PLAN_TABS.map(t => (
          <Tab key={t.key} icon={t.icon} iconPosition="start" label={t.label}
            sx={{ textTransform: 'none', minHeight: 48, fontSize: '0.8rem' }} />
        ))}
      </Tabs>

      <Box sx={{ p: 2 }}>
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1.5 }}>
          {PLAN_HINTS[currentKey]}
        </Typography>

        <TextField
          fullWidth
          multiline
          minRows={5}
          size="small"
          value={texts[currentKey]}
          onChange={handleTextChange}
          placeholder={`Describe your ${PLAN_TABS[planTab].label.toLowerCase()}...`}
        />

        <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
          <Button
            size="small"
            variant="contained"
            startIcon={saving ? <CircularProgress size={14} color="inherit" /> : <SaveIcon />}
            onClick={() => save(texts)}
            disabled={saving}
            sx={{ textTransform: 'none' }}
          >
            {saving ? 'Saving…' : saved ? 'Saved!' : 'Save'}
          </Button>
        </Box>
      </Box>
    </Paper>
  );
}

export default function DisseminationTab({ proposal }) {
  // Flatten all deliverables from all WPs
  const allDeliverables = (proposal?.work_packages || []).flatMap(wp =>
    (wp.deliverables || []).map(d => ({
      ...d,
      wp_number: wp.number,
      wp_title: wp.title,
    }))
  );

  const theme = useTheme();
  const [activities, setActivities] = useState({});   // keyed by deliverable id
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editTarget, setEditTarget] = useState(null);  // { deliverable, activity? }
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  // ── KER state ──────────────────────────────────────────────────────────────
  const [kers, setKers] = useState(() => proposal?.dissemination_plan?.key_exploitable_results || []);
  const [kerDialogOpen, setKerDialogOpen] = useState(false);
  const [editingKer, setEditingKer] = useState(null);  // { index, ker } | null
  const [kerSaving, setKerSaving] = useState(false);

  useEffect(() => {
    setKers(proposal?.dissemination_plan?.key_exploitable_results || []);
  }, [proposal?.id]);

  const saveKers = useCallback(async (nextKers) => {
    if (!proposal?.id) return;
    setKerSaving(true);
    try {
      await axiosService.patch(`/proposal/proposals/${proposal.id}/`, {
        dissemination_plan: { ...(proposal.dissemination_plan || {}), key_exploitable_results: nextKers },
      });
    } catch (_) {}
    finally { setKerSaving(false); }
  }, [proposal?.id, proposal?.dissemination_plan]);

  const handleSaveKer = (kerData) => {
    const next = editingKer !== null
      ? kers.map((k, i) => (i === editingKer.index ? kerData : k))
      : [...kers, kerData];
    setKers(next);
    saveKers(next);
    setKerDialogOpen(false);
    setEditingKer(null);
  };

  const handleDeleteKer = (idx) => {
    const next = kers.filter((_, i) => i !== idx);
    setKers(next);
    saveKers(next);
  };

  const loadActivities = useCallback(async () => {
    if (!proposal?.id) return;
    setLoading(true);
    try {
      const res = await axiosService.get('/proposal/dissemination/', { params: { proposal: proposal.id } });
      const items = res.data?.results || res.data || [];
      const byDeliverable = {};
      items.forEach(item => {
        const key = String(item.deliverable);
        if (!byDeliverable[key]) byDeliverable[key] = [];
        byDeliverable[key].push(item);
      });
      setActivities(byDeliverable);
    } catch (_) {
      /* ignore */
    } finally {
      setLoading(false);
    }
  }, [proposal?.id]);

  useEffect(() => { loadActivities(); }, [loadActivities]);

  const handleDelete = async (id) => {
    try {
      await axiosService.delete(`/proposal/dissemination/${id}/`);
      loadActivities();
    } catch (_) { /* ignore */ }
    setDeleteConfirm(null);
  };

  if (allDeliverables.length === 0) {
    return (
      <Paper variant="outlined" sx={{ borderRadius: 1, overflow: 'hidden' }}>
        <Box sx={{ px: 2, py: 1.5, borderBottom: '1px solid', borderColor: 'divider' }}>
          <Typography variant="subtitle2" fontWeight={600}>Dissemination &amp; Exploitation Activities</Typography>
          <Typography variant="caption" color="text.secondary">Activities planned per deliverable</Typography>
        </Box>
        <Box sx={{ p: 4, textAlign: 'center' }}>
          <DisseminationIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
          <Typography color="text.secondary">No deliverables yet. Add work packages and tasks with deliverables first.</Typography>
        </Box>
      </Paper>
    );
  }

  return (
    <Paper variant="outlined" sx={{ borderRadius: 1, overflow: 'hidden', pt:1 }}>
      {/* ── Header ── */}
      <Box sx={{ px: 2, py: 1.5, borderBottom: '1px solid', borderColor: 'divider', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box>
          <Typography variant="subtitle2" fontWeight={600}>Dissemination &amp; Exploitation Activities</Typography>
          <Typography variant="caption" color="text.secondary">Activities planned per deliverable</Typography>
        </Box>
      </Box>
      <Box sx={{ p: 2 }}>

      {/* ── KER table ── */}
      {kers.length > 0 && (
        <Paper variant="outlined" sx={{ mb: 2, borderRadius: 1, overflow: 'hidden' }}>
          <Box sx={{ px: 2, py: 1, borderBottom: '1px solid', borderColor: 'divider' }}>
            <Typography variant="caption" fontWeight={600} color="text.secondary">
              KEY EXPLOITABLE RESULTS
            </Typography>
          </Box>
          <Box sx={{ overflowX: 'auto' }}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ '& th': { fontWeight: 600 } }}>
                  <TableCell>Title</TableCell>
                  <TableCell>TRL Now → Target</TableCell>
                  <TableCell>Exploitation Route</TableCell>
                  <TableCell>Owner</TableCell>
                  <TableCell align="right" />
                </TableRow>
              </TableHead>
              <TableBody>
                {kers.map((ker, i) => (
                  <TableRow key={i} hover>
                    <TableCell>
                      <Typography variant="body2" fontWeight={500}>{ker.title}</Typography>
                      {ker.description && <Typography variant="caption" color="text.secondary">{ker.description}</Typography>}
                    </TableCell>
                    <TableCell>
                      {ker.trl_current && ker.trl_target
                        ? `TRL ${ker.trl_current} → TRL ${ker.trl_target}`
                        : ker.trl_current ? `TRL ${ker.trl_current}` : '—'}
                    </TableCell>
                    <TableCell>
                      {EXPLOITATION_ROUTES.find(r => r.value === ker.exploitation_route)?.label || ker.exploitation_route || '—'}
                    </TableCell>
                    <TableCell>{ker.owner || '—'}</TableCell>
                    <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                      <Tooltip title="Edit">
                        <IconButton size="small" onClick={() => { setEditingKer({ index: i, ker }); setKerDialogOpen(true); }}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete">
                        <IconButton size="small" color="error" onClick={() => handleDeleteKer(i)}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
        </Paper>
      )}

      {loading && <Box sx={{ display: 'flex', justifyContent: 'center', py: 1 }}><CircularProgress size={24} /></Box>}

      <Stack spacing={0} sx={{pb:0}}>
        {allDeliverables.map((deliverable) => {
          const delivActivities = activities[String(deliverable.id)] || [];
          const catDef = CATEGORIES.find(c => c.value === deliverable.category);

          return (
            <Accordion key={deliverable.id} defaultExpanded={delivActivities.length > 0} 
              sx={{ boxShadow: 'none', '&:before': { display: 'none' }, '& + &': { borderTop: '1px solid', borderColor: 'divider' } }}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{
                px: 2, minHeight: 52,
                '& .MuiAccordionSummary-content': { minWidth: 0, overflow: 'hidden' },
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1, mr: 1, minWidth: 0, overflow: 'hidden' }}>
                  <Typography variant="subtitle2" fontWeight={600} sx={{ flexShrink: 0 }}>
                    D{deliverable.wp_number}.{deliverable.number}
                  </Typography>
                  <Typography variant="body2" noWrap sx={{ flex: 1, minWidth: 0 }}>
                    {deliverable.title || deliverable.description || 'Untitled Deliverable'}
                  </Typography>
                  <Chip label={catDef?.label || deliverable.type} size="small" variant="outlined"
                    sx={{ flexShrink: 0, display: { xs: 'none', sm: 'inline-flex' } }} />
                  <Chip label={`${delivActivities.length} activities`} size="small"
                    color={delivActivities.length > 0 ? 'primary' : 'default'}
                    variant={delivActivities.length > 0 ? 'filled' : 'outlined'}
                    sx={{ flexShrink: 0, display: { xs: 'none', md: 'inline-flex' } }} />
                </Box>
              </AccordionSummary>
              <AccordionDetails sx={{ p: 0 }}>
                <Divider />
                {delivActivities.length === 0 ? (
                  <Typography variant="body2" color="text.secondary" sx={{ px: 2, pb: 1 }}>
                    No dissemination activities yet.
                  </Typography>
                ) : (
                  <List disablePadding>
                    {delivActivities.map((act, actIdx) => (
                      <ListItem key={act.id} sx={{ py: 1, px: 2, display: 'flex', alignItems: 'center', gap: 1, bgcolor: theme.palette.primary.slight }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', mr: 0.5 }}>
                          {CATEGORY_ICON[act.category]}
                        </Box>
                        <ListItemText
                          sx={{ flex: 1, minWidth: 0 }}
                          primary={
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                              <Typography variant="body2" fontWeight={500}>
                                {act.title || act.category_display}
                              </Typography>
                              {act.event_title && (
                                <Chip icon={<EventIcon sx={{ fontSize: '13px !important' }} />}
                                  label={act.event_title} size="small" color="info" variant="outlined" />
                              )}
                              {act.target_audience && (
                                <Chip
                                  label={
                                    act.target_audience === 'other' && act.target_audience_other
                                      ? act.target_audience_other
                                      : AUDIENCES.find(a => a.value === act.target_audience)?.label || act.target_audience
                                  }
                                  size="small" variant="outlined"
                                />
                              )}
                            </Box>
                          }
                          secondary={act.description || undefined}
                        />
                        <Box sx={{ display: 'flex', flexShrink: 0 }}>
                          <Tooltip title="Edit">
                            <IconButton size="small" onClick={() => { setEditTarget({ deliverable, activity: act }); setDialogOpen(true); }}>
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Delete">
                            <IconButton size="small" color="error" onClick={() => setDeleteConfirm(act.id)}>
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </ListItem>
                    ))}
                  </List>
                )}
                <Box sx={{ px: 2, py: 1.5 }}>
                  <Button size="small" startIcon={<AddIcon />} variant="outlined"
                    onClick={() => { setEditTarget({ deliverable, activity: null }); setDialogOpen(true); }}>
                    Add Dissemination Activity
                  </Button>


                  <Button
                        size="small"
                        startIcon={kerSaving ? <CircularProgress size={14} color="inherit" /> : <AddIcon />}
                        variant="outlined"
                        onClick={() => { setEditingKer(null); setKerDialogOpen(true); }}
                        disabled={kerSaving}
                        sx={{  ml: 2 }}
                      >
                  Add Key Exploitable Result (KER)
            </Button>

                </Box>
              </AccordionDetails>
            </Accordion>
          );
        })}
      </Stack>

      <ActivityDialog
        open={dialogOpen}
        onClose={() => { setDialogOpen(false); setEditTarget(null); }}
        deliverable={editTarget?.deliverable}
        activity={editTarget?.activity}
        onSaved={loadActivities}
      />

      <KERDialog
        open={kerDialogOpen}
        onClose={() => { setKerDialogOpen(false); setEditingKer(null); }}
        ker={editingKer?.ker || null}
        onSave={handleSaveKer}
      />

      {/* Delete confirm */}
      <Dialog open={Boolean(deleteConfirm)} onClose={() => setDeleteConfirm(null)} maxWidth="xs" sx={{ zIndex: 3100 }}>
        <DialogTitle>Delete activity?</DialogTitle>
        <DialogContent><Typography>This cannot be undone.</Typography></DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirm(null)}>Cancel</Button>
          <Button color="error" variant="contained" onClick={() => handleDelete(deleteConfirm)}>Delete</Button>
        </DialogActions>
      </Dialog>
    </Box>
    </Paper>
  );
}
